import { useState, useEffect } from 'react';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import DashboardLayout from '../components/DashboardLayout';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Textarea } from '../components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter, DialogClose } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Plus, Edit, Trash2, FileText, Search, Loader2 } from 'lucide-react';
import { format } from 'date-fns';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const CATEGORIES = ['Dispute', 'Goodwill', 'Validation', 'Negotiation', 'Legal', 'Fraud', 'Request', 'Reference'];

export default function LettersPage() {
  const { user } = useAuth();
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [showAddDialog, setShowAddDialog] = useState(false);
  const [showEditDialog, setShowEditDialog] = useState(false);
  const [editingTemplate, setEditingTemplate] = useState(null);
  const [formLoading, setFormLoading] = useState(false);
  
  const [formData, setFormData] = useState({
    name: '',
    category: 'Dispute',
    description: '',
    content: ''
  });

  const canManage = ['aline', 'administrative'].includes(user?.role);

  useEffect(() => {
    fetchTemplates();
  }, []);

  const fetchTemplates = async () => {
    try {
      const { data } = await axios.get(`${API_URL}/api/letter-templates`);
      setTemplates(data);
    } catch (error) {
      console.error('Error fetching templates:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleAddTemplate = async (e) => {
    e.preventDefault();
    setFormLoading(true);
    try {
      await axios.post(`${API_URL}/api/letter-templates`, formData);
      setShowAddDialog(false);
      setFormData({ name: '', category: 'Dispute', description: '', content: '' });
      fetchTemplates();
    } catch (error) {
      console.error('Error adding template:', error);
    } finally {
      setFormLoading(false);
    }
  };

  const handleEditTemplate = async (e) => {
    e.preventDefault();
    setFormLoading(true);
    try {
      await axios.put(`${API_URL}/api/letter-templates/${editingTemplate.id}`, formData);
      setShowEditDialog(false);
      setEditingTemplate(null);
      setFormData({ name: '', category: 'Dispute', description: '', content: '' });
      fetchTemplates();
    } catch (error) {
      console.error('Error updating template:', error);
    } finally {
      setFormLoading(false);
    }
  };

  const handleDeleteTemplate = async (templateId) => {
    if (!window.confirm('Are you sure you want to delete this template?')) return;
    try {
      await axios.delete(`${API_URL}/api/letter-templates/${templateId}`);
      fetchTemplates();
    } catch (error) {
      console.error('Error deleting template:', error);
    }
  };

  const openEditDialog = (template) => {
    setEditingTemplate(template);
    setFormData({
      name: template.name,
      category: template.category,
      description: template.description,
      content: template.content
    });
    setShowEditDialog(true);
  };

  const filteredTemplates = templates.filter(t => {
    const matchesSearch = t.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                          t.description?.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesCategory = selectedCategory === 'all' || t.category === selectedCategory;
    return matchesSearch && matchesCategory;
  });

  const groupedByCategory = CATEGORIES.reduce((acc, cat) => {
    acc[cat] = filteredTemplates.filter(t => t.category === cat);
    return acc;
  }, {});

  return (
    <DashboardLayout>
      <div className="space-y-6" data-testid="letters-page">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className="text-3xl font-semibold text-slate-900">Letter Templates</h1>
            <p className="text-slate-500 mt-1">Manage credit repair letter templates</p>
          </div>
          {canManage && (
            <Dialog open={showAddDialog} onOpenChange={setShowAddDialog}>
              <DialogTrigger asChild>
                <Button className="bg-blue-600 hover:bg-blue-700 text-white" data-testid="add-template-btn">
                  <Plus className="w-4 h-4 mr-2" />
                  Add Template
                </Button>
              </DialogTrigger>
              <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
                <DialogHeader>
                  <DialogTitle>Add Letter Template</DialogTitle>
                </DialogHeader>
                <form onSubmit={handleAddTemplate} className="space-y-4">
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label htmlFor="name">Template Name</Label>
                      <Input
                        id="name"
                        value={formData.name}
                        onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                        required
                        data-testid="input-template-name"
                      />
                    </div>
                    <div>
                      <Label htmlFor="category">Category</Label>
                      <Select
                        value={formData.category}
                        onValueChange={(value) => setFormData({ ...formData, category: value })}
                      >
                        <SelectTrigger data-testid="select-category">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {CATEGORIES.map(cat => (
                            <SelectItem key={cat} value={cat}>{cat}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                  <div>
                    <Label htmlFor="description">Description</Label>
                    <Input
                      id="description"
                      value={formData.description}
                      onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                      data-testid="input-description"
                    />
                  </div>
                  <div>
                    <Label htmlFor="content">Letter Content</Label>
                    <Textarea
                      id="content"
                      value={formData.content}
                      onChange={(e) => setFormData({ ...formData, content: e.target.value })}
                      rows={12}
                      className="font-mono text-sm"
                      placeholder="Use [Your Full Name], [Your Address], [Date], etc. as placeholders"
                      required
                      data-testid="input-content"
                    />
                  </div>
                  <DialogFooter>
                    <DialogClose asChild>
                      <Button variant="outline">Cancel</Button>
                    </DialogClose>
                    <Button type="submit" className="bg-blue-600 hover:bg-blue-700" disabled={formLoading} data-testid="submit-template">
                      {formLoading && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
                      Add Template
                    </Button>
                  </DialogFooter>
                </form>
              </DialogContent>
            </Dialog>
          )}
        </div>

        {/* Search and Filter */}
        <Card className="bg-white border-slate-200 shadow-sm">
          <CardContent className="p-4">
            <div className="flex flex-col sm:flex-row gap-4">
              <div className="relative flex-1">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-slate-400" />
                <Input
                  placeholder="Search templates..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-10"
                  data-testid="search-templates"
                />
              </div>
              <Select value={selectedCategory} onValueChange={setSelectedCategory}>
                <SelectTrigger className="w-full sm:w-48" data-testid="filter-category">
                  <SelectValue placeholder="All Categories" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Categories</SelectItem>
                  {CATEGORIES.map(cat => (
                    <SelectItem key={cat} value={cat}>{cat}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </CardContent>
        </Card>

        {/* Templates Grid */}
        {loading ? (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
          </div>
        ) : (
          <Tabs defaultValue="all" className="space-y-4">
            <TabsList className="bg-slate-100 flex-wrap h-auto gap-1 p-1">
              <TabsTrigger value="all" data-testid="tab-all">
                All ({filteredTemplates.length})
              </TabsTrigger>
              {CATEGORIES.map(cat => (
                <TabsTrigger key={cat} value={cat} data-testid={`tab-${cat.toLowerCase()}`}>
                  {cat} ({groupedByCategory[cat]?.length || 0})
                </TabsTrigger>
              ))}
            </TabsList>

            <TabsContent value="all">
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {filteredTemplates.map((template) => (
                  <TemplateCard
                    key={template.id}
                    template={template}
                    canManage={canManage}
                    onEdit={() => openEditDialog(template)}
                    onDelete={() => handleDeleteTemplate(template.id)}
                  />
                ))}
              </div>
            </TabsContent>

            {CATEGORIES.map(cat => (
              <TabsContent key={cat} value={cat}>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {groupedByCategory[cat]?.map((template) => (
                    <TemplateCard
                      key={template.id}
                      template={template}
                      canManage={canManage}
                      onEdit={() => openEditDialog(template)}
                      onDelete={() => handleDeleteTemplate(template.id)}
                    />
                  ))}
                </div>
              </TabsContent>
            ))}
          </Tabs>
        )}

        {/* Edit Dialog */}
        <Dialog open={showEditDialog} onOpenChange={setShowEditDialog}>
          <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>Edit Letter Template</DialogTitle>
            </DialogHeader>
            <form onSubmit={handleEditTemplate} className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="edit-name">Template Name</Label>
                  <Input
                    id="edit-name"
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    required
                    data-testid="edit-template-name"
                  />
                </div>
                <div>
                  <Label htmlFor="edit-category">Category</Label>
                  <Select
                    value={formData.category}
                    onValueChange={(value) => setFormData({ ...formData, category: value })}
                  >
                    <SelectTrigger data-testid="edit-category">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {CATEGORIES.map(cat => (
                        <SelectItem key={cat} value={cat}>{cat}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div>
                <Label htmlFor="edit-description">Description</Label>
                <Input
                  id="edit-description"
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  data-testid="edit-description"
                />
              </div>
              <div>
                <Label htmlFor="edit-content">Letter Content</Label>
                <Textarea
                  id="edit-content"
                  value={formData.content}
                  onChange={(e) => setFormData({ ...formData, content: e.target.value })}
                  rows={12}
                  className="font-mono text-sm"
                  required
                  data-testid="edit-content"
                />
              </div>
              <DialogFooter>
                <DialogClose asChild>
                  <Button variant="outline">Cancel</Button>
                </DialogClose>
                <Button type="submit" className="bg-blue-600 hover:bg-blue-700" disabled={formLoading} data-testid="submit-edit">
                  {formLoading && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
                  Save Changes
                </Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>
      </div>
    </DashboardLayout>
  );
}

function TemplateCard({ template, canManage, onEdit, onDelete }) {
  const categoryColors = {
    Dispute: 'bg-blue-100 text-blue-800',
    Goodwill: 'bg-emerald-100 text-emerald-800',
    Validation: 'bg-purple-100 text-purple-800',
    Negotiation: 'bg-amber-100 text-amber-800',
    Legal: 'bg-rose-100 text-rose-800',
    Fraud: 'bg-red-100 text-red-800',
    Request: 'bg-cyan-100 text-cyan-800',
    Reference: 'bg-slate-100 text-slate-800'
  };

  return (
    <Card className="bg-white border-slate-200 shadow-sm hover:shadow-md transition-shadow" data-testid={`template-card-${template.id}`}>
      <CardContent className="p-6">
        <div className="flex items-start justify-between mb-3">
          <div className="w-10 h-10 rounded-lg bg-blue-50 flex items-center justify-center">
            <FileText className="w-5 h-5 text-blue-600" />
          </div>
          <Badge className={categoryColors[template.category] || 'bg-slate-100 text-slate-800'}>
            {template.category}
          </Badge>
        </div>
        <h3 className="font-semibold text-slate-900 mb-1">{template.name}</h3>
        <p className="text-sm text-slate-500 mb-4 line-clamp-2">{template.description}</p>
        {canManage && (
          <div className="flex items-center gap-2 pt-4 border-t border-slate-100">
            <Button variant="outline" size="sm" onClick={onEdit} data-testid={`edit-template-${template.id}`}>
              <Edit className="w-4 h-4 mr-1" />
              Edit
            </Button>
            <Button variant="outline" size="sm" className="text-rose-600 hover:text-rose-700" onClick={onDelete} data-testid={`delete-template-${template.id}`}>
              <Trash2 className="w-4 h-4" />
            </Button>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
